import { useDispatch } from 'react-redux';

// material-ui
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputLabel,
  Stack,
  TextField,
  Tooltip
} from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// third-party
import * as Yup from 'yup';
import { Form, FormikProvider, FormikValues, useFormik } from 'formik';

// project imports
import { openSnackbar } from 'store/reducers/snackbar';

// assets
import {
  InvestorBalanceChartDocument,
  InvestorBalanceDocument,
  TransactionType,
  useDeleteCashTransactionMutation,
  usePaymentMethodsQuery,
  useSaveCashTransactionMutation
} from '../../graphql/types';
import CurrencyFormatter from '../../components/input-formatters/CurrencyFormatter';
import React from 'react';
import { DeleteFilled } from '@ant-design/icons';
import { underscoreToSpace } from '../../utils/strings';

// constant
const getInitialValues = (entity: FormikValues | null) => {
  const newEntity = {
    amount: 0,
    type: TransactionType.FundingWallet,
    date: new Date(),
    notes: '',
    paymentMethodId: 'MANUAL'
  };

  if (entity) {
    newEntity.amount = entity.amount;
    newEntity.type = entity.type;
    newEntity.date = entity.depositDate;
    newEntity.notes = entity.notes;
    newEntity.paymentMethodId = entity.paymentMethodId;
  }
  return newEntity;
};

export interface Props {
  entity?: any;
  investorUserId: string;
  onCancel: () => void;
  onSave: (transaction: any) => void;
  forLastMonths: number;
}

const AddCashTransaction = ({ investorUserId, entity, onSave, onCancel, forLastMonths }: Props) => {
  const dispatch = useDispatch();
  const [updateTransaction] = useSaveCashTransactionMutation();
  const [deleteTransaction] = useDeleteCashTransactionMutation();
  const { data: paymentMethodsData } = usePaymentMethodsQuery();
  const paymentMethods = paymentMethodsData?.paymentMethods || [];
  const ValidationSchema = Yup.object().shape({
    amount: Yup.number()
      .required('Transaction amount is required')
      .test('is-non-zero', 'Amount must be non-zero', (value) => value !== 0),
    paymentMethodId: Yup.string().required('Payment Method is required'),
    type: Yup.string().required('Type is required')
  });

  const deleteHandler = async () => {
    try {
      await deleteTransaction({
        variables: { id: entity.id },
        refetchQueries: [
          {
            query: InvestorBalanceDocument,
            variables: { userId: investorUserId }
          },
          { query: InvestorBalanceChartDocument, variables: { userId: investorUserId, forLastMonths } }
        ]
      });
      onSave(entity);
      dispatch(
        openSnackbar({
          open: true,
          message: 'Transaction deleted successfully.',
          variant: 'alert',
          alert: {
            color: 'success'
          },
          close: true
        })
      );
      onCancel();
    } catch (error) {
      console.error(error);
    }
  };

  const formik = useFormik({
    initialValues: getInitialValues(entity!),
    validationSchema: ValidationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const saved = await updateTransaction({
          variables: {
            dto: {
              id: entity ? entity.id : null,
              amount: parseFloat(values.amount.toString()),
              date: values.date,
              type: values.type,
              notes: values.notes,
              paymentMethodId: values.paymentMethodId,
              assignedToUserId: investorUserId
            }
          },
          refetchQueries: [
            {
              query: InvestorBalanceDocument,
              variables: { userId: investorUserId, forLastMonths }
            },
            { query: InvestorBalanceChartDocument, variables: { userId: investorUserId, forLastMonths } }
          ]
        });
        onSave(saved);
        if (entity) {
          dispatch(
            openSnackbar({
              open: true,
              message: 'Transaction updated successfully.',
              variant: 'alert',
              alert: {
                color: 'success'
              },
              close: true
            })
          );
        } else {
          dispatch(
            openSnackbar({
              open: true,
              message: 'Transaction added successfully.',
              variant: 'alert',
              alert: {
                color: 'success'
              },
              close: true
            })
          );
        }

        setSubmitting(false);
        onCancel();
      } catch (error) {
        console.error(error);
      }
    }
  });

  const { errors, touched, values, handleSubmit, isSubmitting, getFieldProps, handleBlur, setFieldValue, handleChange } = formik;

  return (
    <FormikProvider value={formik}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <DialogTitle>{entity ? 'Edit Wallet Transaction' : 'New Wallet Transaction'}</DialogTitle>
          <Divider />
          <DialogContent sx={{ p: 2.5 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Stack spacing={1.25}>
                  <InputLabel required={true} htmlFor="ManualTransaction-entity-amount">
                    Amount
                  </InputLabel>
                  <TextField
                    variant="filled"
                    fullWidth
                    id="ManualTransaction-entity-amount"
                    placeholder="Enter Transaction Amount"
                    {...getFieldProps('amount')}
                    error={Boolean(touched.amount && errors.amount)}
                    helperText={touched.amount && errors.amount}
                    InputProps={{
                      inputComponent: CurrencyFormatter as any
                    }}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1.25}>
                  <InputLabel required={true} htmlFor="ManualTransaction-entity-date">
                    Date
                  </InputLabel>
                  <DesktopDatePicker
                    value={values.date ? new Date(values.date) : null}
                    onChange={(value) => setFieldValue('date', value)}
                    slotProps={{
                      textField: {
                        variant: 'filled',
                        error: Boolean(touched.date && errors.date),
                        helperText: touched.date && (errors.date ? <>{errors.date}</> : ''),
                        name: 'date',
                        onBlur: handleBlur
                      }
                    }}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={2} direction={'row'}>
                  <Stack spacing={1.25} sx={{ width: '50%' }}>
                    <InputLabel required={true} htmlFor="ManualTransaction-entity-type">
                      Type
                    </InputLabel>
                    <TextField
                      id="ManualTransaction-entity-type"
                      variant="filled"
                      error={Boolean(touched.type && errors.type)}
                      helperText={touched.type && errors.type}
                      fullWidth
                      name="type"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.type}
                      select
                      SelectProps={{ native: true }}
                    >
                      <option value={''}>Select Type</option>
                      {[
                        TransactionType.FundingWallet,
                        TransactionType.Remittance,
                        TransactionType.RemittanceFee,
                        TransactionType.CrmAccessFee
                      ].map((type) => (
                        <option key={type} value={type}>
                          {type.split('_').join(' ')}
                        </option>
                      ))}
                    </TextField>
                  </Stack>
                  <Stack spacing={1.25} sx={{ width: '50%' }}>
                    <InputLabel required={true} htmlFor="ManualTransaction-entity-paymentMethodId">
                      Payment Method
                    </InputLabel>
                    <TextField
                      id="ManualTransaction-entity-paymentMethodId"
                      variant="filled"
                      error={Boolean(touched.paymentMethodId && errors.paymentMethodId)}
                      helperText={touched.paymentMethodId && errors.paymentMethodId}
                      fullWidth
                      name="paymentMethodId"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.paymentMethodId}
                      select
                      SelectProps={{ native: true }}
                    >
                      <option value={''}>Select Payment Method</option>
                      {paymentMethods
                        .filter((method) => !['MANUAL', 'SYNDICATION', 'IN_HOUSE_LEGAL', 'BALANCE_REFI', 'ATTORNEY'].includes(method.id))
                        .sort((a, b) => a.order - b.order)
                        .map((method) => (
                          <option key={method.id} value={method.id}>
                            {underscoreToSpace(method.id).trim()}
                          </option>
                        ))}
                    </TextField>
                  </Stack>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1.25}>
                  <InputLabel htmlFor="ManualTransaction-entity-description">Notes</InputLabel>
                  <TextField
                    variant="filled"
                    fullWidth
                    id="ManualTransaction-entity-description"
                    placeholder="Enter Notes"
                    {...getFieldProps('notes')}
                    error={Boolean(touched.notes && errors.notes)}
                    helperText={touched.notes && errors.notes}
                  />
                </Stack>
              </Grid>
            </Grid>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ p: 2.5 }}>
            <Grid container justifyContent="space-between" alignItems="center">
              {
                <Grid item>
                  {entity && (
                    <Tooltip title="Delete Transaction" placement="top">
                      <IconButton onClick={deleteHandler} size="large" color="error">
                        <DeleteFilled />
                      </IconButton>
                    </Tooltip>
                  )}
                </Grid>
              }
              <Grid item>&nbsp;</Grid>
              <Grid item>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Button color="error" onClick={onCancel}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained" disabled={isSubmitting}>
                    {entity ? 'Save Transaction' : 'Add Transaction'}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </DialogActions>
        </Form>
      </LocalizationProvider>
    </FormikProvider>
  );
};

export default AddCashTransaction;
